<template>
  <section class="demo-page">
    <div class="demo-shell" :class="{ 'is-gated': !isUnlocked }">
      <header class="demo-page__header">
        <p class="demo-page__eyebrow">HarborGate Demo</p>
        <h1 class="demo-page__title">
          {{ isUnlocked ? 'Choose a persona' : 'Demo access' }}
        </h1>
        <p class="demo-page__copy">
          {{
            isUnlocked
              ? 'Sign in with a seeded demo account using the same authenticated session flow as the main app.'
              : 'Enter the shared demo password to view the available personas.'
          }}
        </p>
      </header>

      <form v-if="!isUnlocked" class="demo-gate" @submit.prevent="unlockDemo">
        <b-field
          label="Demo Password"
          :type="passwordError ? 'is-danger' : null"
          :message="passwordError || 'Use the shared demo password to continue.'"
        >
          <b-input
            v-model="password"
            type="password"
            password-reveal
            placeholder="Enter password"
          />
        </b-field>

        <b-button
          type="is-primary"
          native-type="submit"
          :disabled="!password"
        >
          Enter Demo
        </b-button>
      </form>

      <DemoAccessPanel
        v-else
        :initial-demo-key="demoKey"
        @authenticated="handleDemoAuthenticated"
        @demo-key-updated="handleDemoKeyUpdated"
      />

      <div class="demo-page__footer">
        <router-link to="/verify" class="demo-page__link">
          Need regular sign-in? Go to account verification.
        </router-link>
        <button
          v-if="isUnlocked"
          type="button"
          class="demo-page__reset-trigger"
          :disabled="isStartingReset"
          @click="openResetDemoModal"
        >
          Reset demo data
        </button>
      </div>
    </div>

    <b-modal
      v-model="showResetDemoModal"
      :can-cancel="['escape', 'outside', 'button']"
      has-modal-card
      :width="560"
      trap-focus
    >
      <div class="modal-card demo-reset-modal">
        <section class="modal-card-body demo-reset-modal__body">
          <template v-if="resetModalMode === 'status'">
            <div class="demo-reset-modal__header">
              <div>
                <p class="demo-reset-modal__eyebrow">Demo Work Refresh</p>
                <h2 class="demo-reset-modal__title">Current reset status</h2>
              </div>
              <span class="demo-reset-modal__status" :class="statusClassName">
                {{ currentJobStatusLabel }}
              </span>
            </div>

            <p class="demo-reset-modal__copy">
              {{ currentJobProgressMessage }}
            </p>

            <p v-if="lastSeededOnLabel" class="demo-reset-modal__meta">
              Last seeded on: {{ lastSeededOnLabel }}
            </p>

            <div v-if="isResetJobRunning" class="demo-reset-modal__loading">
              <b-loading :active="true" :is-full-page="false" />
            </div>

            <div v-if="demoRefreshJob" class="demo-reset-details">
              <div class="demo-reset-details__row">
                <span>Job ID</span>
                <span>{{ demoRefreshJob.jobId || '—' }}</span>
              </div>
              <div class="demo-reset-details__row">
                <span>Stage</span>
                <span>{{ demoRefreshJob.progress?.stage || '—' }}</span>
              </div>
              <div class="demo-reset-details__row">
                <span>Percent</span>
                <span>{{ demoRefreshJob.progress?.percent ?? 0 }}%</span>
              </div>
              <div class="demo-reset-details__row">
                <span>Seed date</span>
                <span>{{ formatJobDate(demoRefreshJob.seedDate) }}</span>
              </div>
              <div class="demo-reset-details__row">
                <span>Created</span>
                <span>{{ formatJobTimestamp(demoRefreshJob.createdAt) }}</span>
              </div>
              <div class="demo-reset-details__row">
                <span>Started</span>
                <span>{{ formatJobTimestamp(demoRefreshJob.startedAt) }}</span>
              </div>
              <div class="demo-reset-details__row">
                <span>Updated</span>
                <span>{{ formatJobTimestamp(demoRefreshJob.updatedAt) }}</span>
              </div>
              <div v-if="demoRefreshJob.completedAt" class="demo-reset-details__row">
                <span>Completed</span>
                <span>{{ formatJobTimestamp(demoRefreshJob.completedAt) }}</span>
              </div>
              <div v-if="demoRefreshJob.lastError" class="demo-reset-details__row demo-reset-details__row--stacked">
                <span>Error</span>
                <span>{{ demoRefreshJob.lastError }}</span>
              </div>
              <div
                v-if="demoRefreshSummary.length"
                class="demo-reset-details__row demo-reset-details__row--stacked"
              >
                <span>Result</span>
                <span>{{ demoRefreshSummary.join(' | ') }}</span>
              </div>
            </div>

            <div class="demo-reset-modal__actions">
              <b-button type="is-text" @click="showResetDemoModal = false">
                Close
              </b-button>
            </div>
          </template>

          <template v-else>
            <p class="demo-reset-modal__eyebrow">Demo Work Refresh</p>
            <h2 class="demo-reset-modal__title">Reset demo data?</h2>
            <p class="demo-reset-modal__copy">
              This will clear and reseed the HarborGate demo work data in the background. Continue only if you want to replace the current demo dataset.
            </p>

            <p v-if="lastSeededOnLabel" class="demo-reset-modal__meta">
              Last seeded on: {{ lastSeededOnLabel }}
            </p>

            <div class="demo-reset-modal__actions">
              <b-button type="is-text" @click="showResetDemoModal = false">
                Cancel
              </b-button>
              <b-button
                type="is-primary"
                :loading="isStartingReset"
                :disabled="isStartingReset"
                @click="confirmResetDemo"
              >
                Confirm reset
              </b-button>
            </div>
          </template>
        </section>
      </div>
    </b-modal>
  </section>
</template>

<script>
import { useDemoStore } from '@/stores/demoStore'
import { useUserStore } from '@/stores/userStore'
import DemoAccessPanel from '@/components/auth/DemoAccessPanel.vue'
import { getDemoWorkRefreshJob, getLatestDemoWorkRefresh, startDemoWorkRefresh } from '@/utils/helpers'

const DEMO_REFRESH_ACTIVE_STATUSES = ['queued', 'clearing', 'seeding']

export default {
  name: 'DemoPage',
  components: {
    DemoAccessPanel,
  },
  data() {
    return {
      password: '',
      passwordError: '',
      isUnlocked: false,
      demoKey: '',
      showResetDemoModal: false,
      resetModalMode: 'confirm',
      demoRefreshJob: null,
      isStartingReset: false,
      refreshPollTimeoutId: null,
    }
  },
  computed: {
    isResetJobRunning() {
      return DEMO_REFRESH_ACTIVE_STATUSES.includes(this.demoRefreshJob?.status)
    },
    currentJobStatusLabel() {
      const status = String(this.demoRefreshJob?.status || '').trim()
      if (!status) return 'Unknown'

      return status
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    },
    currentJobProgressMessage() {
      return this.demoRefreshJob?.progress?.message || 'No job details are available yet.'
    },
    lastSeededOnLabel() {
      const seededAt = this.demoRefreshJob?.result?.seeded?.seededAt || this.demoRefreshJob?.completedAt
      return seededAt ? this.formatJobTimestamp(seededAt) : ''
    },
    statusClassName() {
      const status = this.demoRefreshJob?.status

      return {
        'is-running': DEMO_REFRESH_ACTIVE_STATUSES.includes(status),
        'is-completed': status === 'completed',
        'is-failed': status === 'failed',
      }
    },
    demoRefreshSummary() {
      const seeded = this.demoRefreshJob?.result?.seeded || {}
      const cleared = this.demoRefreshJob?.result?.cleared || {}
      const summary = []

      if (Number.isFinite(Number(cleared.ticketRowsCleared))) {
        summary.push(`${cleared.ticketRowsCleared} rows cleared`)
      }
      if (Number.isFinite(Number(seeded.tickets))) {
        summary.push(`${seeded.tickets} tickets seeded`)
      }
      if (Number.isFinite(Number(seeded.qdrantTickets))) {
        summary.push(`${seeded.qdrantTickets} vectors seeded`)
      }
      if (Array.isArray(seeded.warnings) && seeded.warnings.length) {
        summary.push(`${seeded.warnings.length} warnings`)
      }

      return summary
    },
  },
  async created() {
    const demoStore = useDemoStore()
    this.isUnlocked = demoStore.isUnlocked
    this.demoKey = demoStore.demoKey
    this.demoRefreshJob = demoStore.refreshJob

    const token = this.$cookies.get('teamwrkr-token')

    if (token) {
      const userStore = useUserStore()

      try {
        await userStore.fetchUser(token)

        if (userStore.user?._id) {
          this.$router.push('/')
          return
        }
      } catch (error) {
        this.$cookies.remove('teamwrkr-token')
        userStore.$reset()
      }
    }

    if (this.demoRefreshJob?.jobId && this.isResetJobRunning) {
      this.beginRefreshPolling()
      return
    }

    await this.loadLatestRefreshJob()
  },
  beforeUnmount() {
    this.clearRefreshPolling()
  },
  methods: {
    setDemoRefreshJob(job) {
      this.demoRefreshJob = job || null
      useDemoStore().setRefreshJob(this.demoRefreshJob)
    },
    unlockDemo() {
      if (this.password !== 'test') {
        this.passwordError = 'Incorrect password.'
        return
      }

      this.passwordError = ''
      this.isUnlocked = true
      useDemoStore().setUnlocked(true)
    },
    handleDemoKeyUpdated(demoKey) {
      this.demoKey = String(demoKey || '').trim()
      useDemoStore().setDemoKey(this.demoKey)

      if (!this.isResetJobRunning) {
        this.loadLatestRefreshJob()
      }
    },
    handleDemoAuthenticated(user) {
      const userStore = useUserStore()
      userStore.setUser(user)
      this.$cookies.set('teamwrkr-token', user.token)
      this.$router.push('/')
    },
    openResetDemoModal() {
      if (this.isResetJobRunning) {
        this.resetModalMode = 'status'
      } else {
        this.resetModalMode = 'confirm'
      }

      this.showResetDemoModal = true
    },
    async loadLatestRefreshJob() {
      try {
        const payload = await getLatestDemoWorkRefresh(this.demoKey)
        this.setDemoRefreshJob(payload?.job || payload)
      } catch (error) {
        const status = Number(error?.response?.status || 0)

        if (status === 401) {
          return
        }

        if (status === 404) {
          this.setDemoRefreshJob(null)
          return
        }

        this.$buefy.toast.open({
          message: error?.response?.data?.message || 'Unable to load the latest demo reset status.',
          type: 'is-danger',
        })
      }
    },
    async confirmResetDemo() {
      if (this.isStartingReset) return

      this.isStartingReset = true

      try {
        const payload = await startDemoWorkRefresh(this.demoKey)
        this.setDemoRefreshJob(payload?.job || payload)
        this.showResetDemoModal = false
        this.$buefy.toast.open({
          message: this.demoRefreshJob?.progress?.message || 'Demo data reset started.',
          type: 'is-info',
        })
        this.beginRefreshPolling()
      } catch (error) {
        const status = Number(error?.response?.status || 0)
        const message = error?.response?.data?.message || 'Unable to start demo work refresh.'
        const existingJob = error?.response?.data?.job || null

        if (status === 409 && existingJob) {
          this.setDemoRefreshJob(existingJob)
          this.showResetDemoModal = false
          this.$buefy.toast.open({
            message,
            type: 'is-warning',
          })
          this.beginRefreshPolling()
          return
        }

        this.$buefy.toast.open({
          message,
          type: 'is-danger',
        })
      } finally {
        this.isStartingReset = false
      }
    },
    beginRefreshPolling() {
      if (!this.demoRefreshJob?.jobId) return

      this.clearRefreshPolling()
      this.pollRefreshJob()
    },
    clearRefreshPolling() {
      if (this.refreshPollTimeoutId) {
        window.clearTimeout(this.refreshPollTimeoutId)
        this.refreshPollTimeoutId = null
      }
    },
    scheduleRefreshPoll() {
      this.clearRefreshPolling()
      this.refreshPollTimeoutId = window.setTimeout(() => {
        this.pollRefreshJob()
      }, 4000)
    },
    async pollRefreshJob() {
      const jobId = this.demoRefreshJob?.jobId
      if (!jobId) return

      try {
        const payload = await getDemoWorkRefreshJob(jobId, this.demoKey)
        this.setDemoRefreshJob(payload?.job || payload)

        if (DEMO_REFRESH_ACTIVE_STATUSES.includes(this.demoRefreshJob?.status)) {
          this.scheduleRefreshPoll()
          return
        }

        this.clearRefreshPolling()

        if (this.demoRefreshJob?.status === 'failed' && this.demoRefreshJob?.lastError) {
          this.$buefy.toast.open({
            message: this.demoRefreshJob.lastError,
            type: 'is-danger',
          })
        }
      } catch (error) {
        this.clearRefreshPolling()
        this.$buefy.toast.open({
          message: error?.response?.data?.message || 'Unable to check demo reset status.',
          type: 'is-danger',
        })
      }
    },
    formatJobTimestamp(value) {
      if (!value) return '—'

      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return String(value)

      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(parsed)
    },
    formatJobDate(value) {
      if (!value) return 'Current time'

      if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
        const [year, month, day] = String(value).split('-').map(Number)
        const parsedDate = new Date(year, month - 1, day)

        return new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(parsedDate)
      }

      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return String(value)

      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(parsed)
    },
  },
}
</script>

<style lang="scss" scoped>
.demo-page {
  min-height: 100vh;
  padding: 3rem 1.25rem;
  background:
    radial-gradient(circle at top left, rgba(170, 118, 214, 0.16), transparent 28%),
    linear-gradient(180deg, $color-surface-soft 0%, $color-surface-alt 100%);
}

.demo-shell {
  width: min(92vw, 980px);
  margin: 0 auto;
  padding: 2rem 2.25rem;
  border: 1px solid $color-border;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: $shadow-lg;
}

.demo-shell.is-gated {
  max-width: 34rem;
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.demo-page__header {
  margin-bottom: 1.5rem;
  text-align: left;
}

.demo-page__eyebrow {
  margin: 0 0 0.4rem;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $color-primary-dark;
}

.demo-page__title {
  margin: 0;
  font-size: clamp(2rem, 3vw, 2.75rem);
  line-height: 1.05;
  color: $color-text-strong;
}

.demo-page__copy {
  max-width: 42rem;
  margin: 0.85rem 0 0;
  font-size: 1rem;
  line-height: 1.6;
  color: $color-text-muted;
}

.demo-gate {
  max-width: 26rem;
  margin: 0 auto;
  width: 100%;
}

.demo-page__footer {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid $color-border;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
}

.demo-page__link {
  font-weight: 700;
  color: $color-primary-dark;
}

.demo-page__reset-trigger {
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 700;
  line-height: 1.5;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  color: $color-text-muted;
}

.demo-page__reset-trigger:disabled {
  opacity: 0.6;
  cursor: default;
}

.demo-reset-modal {
  width: 100%;
  max-width: 560px;
  border-radius: 18px;
  overflow: hidden;
}

.demo-reset-modal__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
}

.demo-reset-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.demo-reset-modal__eyebrow {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $color-primary-dark;
}

.demo-reset-modal__title {
  margin: 0.25rem 0 0;
  font-size: 1.45rem;
  color: $color-text-strong;
}

.demo-reset-modal__copy {
  margin: 0;
  color: $color-text-muted;
  line-height: 1.6;
}

.demo-reset-modal__meta {
  margin: -0.25rem 0 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: $color-text-strong;
}

.demo-reset-modal__status {
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: $color-surface-soft;
  color: $color-text;
}

.demo-reset-modal__status.is-running {
  background: rgba(49, 130, 206, 0.12);
  color: #1d4f91;
}

.demo-reset-modal__status.is-completed {
  background: rgba(56, 161, 105, 0.12);
  color: #276749;
}

.demo-reset-modal__status.is-failed {
  background: rgba(229, 62, 62, 0.12);
  color: #9b2c2c;
}

.demo-reset-modal__loading {
  position: relative;
  min-height: 3rem;
}

.demo-reset-details {
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid $color-border;
  border-radius: 14px;
  background: $color-surface-soft;
}

.demo-reset-details__row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.95rem;
  color: $color-text;
}

.demo-reset-details__row span:first-child {
  font-weight: 700;
  color: $color-text-strong;
}

.demo-reset-details__row span:last-child {
  text-align: right;
  color: $color-text-muted;
}

.demo-reset-details__row--stacked {
  flex-direction: column;
}

.demo-reset-details__row--stacked span:last-child {
  text-align: left;
}

.demo-reset-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

@media (max-width: 768px) {
  .demo-page {
    padding: 1.5rem 1rem;
  }

  .demo-shell {
    padding: 1.25rem;
    border-radius: 16px;
  }

  .demo-reset-modal__body {
    padding: 1.25rem;
  }

  .demo-reset-modal__header,
  .demo-reset-details__row,
  .demo-reset-modal__actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .demo-reset-details__row span:last-child {
    text-align: left;
  }
}
</style>
